import React, { useState } from "react";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import Tooltip from "@mui/material/Tooltip";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import { formatAndTranslateDate, isExcludedDateActive } from "../../../utils/date";
import ExcludedDate, { getRepeatAnnuallyString } from "../../../interfaces/robot/excluded-dates/excluded-date";
import { DateSorting, getMonthLabelOrNull, getSortingChoices } from "../../../utils/month";
import Chip from "@mui/material/Chip";
import { parseDateString } from "../../../utils/date";
import RoomData from "../../../interfaces/robot/rooms/room-data";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectExcludedDates } from "../../../store/robot-configurations/robot-configurations-selectors";
import ReadOnlyProtectedForm from "../../Inputs/ReadOnlyProtectedForm";
import ReadOnlyProtectedButton from "../../Inputs/ReadOnlyProtectedButton";
import ReadOnlyProtectedIconButton from "../../Inputs/ReadOnlyProtectedIconButton";
import Stack from "@mui/material/Stack";
import { EditIcon, DeleteIcon, AddIcon } from "../../../utils/icons/icons";
import Divider from "@mui/material/Divider";
import { Skeleton } from "@mui/material";

const getFilterFunction = (filterChoice: DateSorting) => {
  const monthLabel = getMonthLabelOrNull(filterChoice);
  if (monthLabel !== null && monthLabel !== undefined) {
    return (d: ExcludedDate) => {
      const startMonth = parseDateString(d.startDate).month;
      const endMonth = parseDateString(d.endDate).month;
      return (
        startMonth === monthLabel.index ||
        endMonth === monthLabel.index ||
        (startMonth < monthLabel.index && endMonth > monthLabel.index && startMonth < endMonth) ||
        ((startMonth < monthLabel.index || endMonth > monthLabel.index) && startMonth > endMonth)
      );
    };
  }
  return (d: ExcludedDate) => d;
};

const ExcludedDatesList: React.FC<{
  availableRooms: RoomData[];
  onAddExcludedDate: (_event: React.MouseEvent) => void;
  onEditDate: (date: ExcludedDate) => void;
}> = ({ availableRooms, onEditDate, onAddExcludedDate }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { deleteExcludedDateById } = useSharedActions();

  const excludedDates = useSharedSelector((state) => selectExcludedDates(state));

  const [sortingChoice, setSortingChoice] = useState(getSortingChoices()[0].value);
  const sortingChoices = getSortingChoices();

  const sortingChangeHandler = (event: SelectChangeEvent<string>) => {
    setSortingChoice(event.target.value as DateSorting);
  };

  const deleteExcludedDateClickHandler = (id: string) => {
    dispatch(deleteExcludedDateById(id));
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <ReadOnlyProtectedButton startIcon={<AddIcon />} onClick={onAddExcludedDate}>
          {t("button-add-no-go-date")}
        </ReadOnlyProtectedButton>
        <FormControl size="small">
          <Select
            labelId="excludedDate-rooms-label"
            fullWidth
            value={sortingChoice}
            onChange={sortingChangeHandler}
            data-testid="select-rooms-id"
          >
            {sortingChoices.map((choice) => (
              <MenuItem key={choice.value} value={choice.value}>
                {choice.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
      <ReadOnlyProtectedForm>
        <Stack spacing={2} divider={<Divider flexItem />}>
          {excludedDates.data == null ? (
            <Stack direction="row">
              <Stack flexGrow={1}>
                <Skeleton variant="text" sx={(theme) => ({ height: theme.spacing(4), width: "40%" })} />
                <Skeleton variant="text" sx={(theme) => ({ height: theme.spacing(3), width: "60%" })} />
                <Skeleton variant="text" sx={(theme) => ({ height: theme.spacing(3), width: "30%" })} />
              </Stack>
              <Stack alignItems="center" spacing={1} direction="row">
                <Skeleton variant="circular" sx={(theme) => ({ height: theme.spacing(5), width: theme.spacing(5) })} />
                <Skeleton variant="circular" sx={(theme) => ({ height: theme.spacing(5), width: theme.spacing(5) })} />
              </Stack>
            </Stack>
          ) : (
            excludedDates.data
              .filter(getFilterFunction(sortingChoice))
              .sort((a, b) => (a.startDate > b.startDate ? 1 : a.startDate < b.startDate ? -1 : 0))
              .map((date) => (
                <Stack direction="row" key={date.id}>
                  <Stack
                    sx={(theme) => ({
                      pl: isExcludedDateActive(date) ? 2 : 0,
                      borderLeft: isExcludedDateActive(date) ? `2px solid ${theme.palette.primary.main}` : undefined,
                    })}
                    flexGrow={1}
                  >
                    <Typography>
                      {formatAndTranslateDate(parseDateString(date.startDate), !date.repeatAnnually)}
                      {parseDateString(date.startDate).startOf("day").toMillis() !==
                        parseDateString(date.endDate).startOf("day").toMillis() &&
                        " - " + formatAndTranslateDate(parseDateString(date.endDate), !date.repeatAnnually)}
                    </Typography>

                    <Typography variant="subtitle2">{`${date.reason}${
                      date.repeatAnnually ? " - " + getRepeatAnnuallyString(parseDateString(date.startDate)) : ""
                    }`}</Typography>
                    <Stack direction="row" spacing={1}>
                      {date.roomIds.length === 0 ? (
                        <Chip label={t("configuration-excludedDates-isGlobal")} size="small" />
                      ) : (
                        date.roomIds.map((roomId) => {
                          const roomMetaInfo = availableRooms.find((r) => r.id === roomId);
                          if (roomMetaInfo !== undefined) {
                            return <Chip key={roomMetaInfo.id} label={roomMetaInfo.name} size="small" />;
                          }
                          return null;
                        })
                      )}
                    </Stack>
                  </Stack>

                  <Stack direction="row" spacing={1} justifyContent="end" flexShrink={0} alignItems="center">
                    <Tooltip title={t("configuration-excludedDates-tooltip-configure") as string}>
                      <span>
                        <ReadOnlyProtectedIconButton color="success" onClick={() => onEditDate(date)}>
                          <EditIcon />
                        </ReadOnlyProtectedIconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={t("configuration-excludedDates-tooltip-delete") as string}>
                      <span>
                        <ReadOnlyProtectedIconButton
                          color="secondary"
                          onClick={() => deleteExcludedDateClickHandler(date.id)}
                        >
                          <DeleteIcon />
                        </ReadOnlyProtectedIconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              ))
          )}
        </Stack>
      </ReadOnlyProtectedForm>
    </Stack>
  );
};

export default ExcludedDatesList;
